import { LandingCtaLink } from '@/landing/landing-cta-link'
import { getLandingIcon } from '@/landing/landing-icon-registry'
import { LANDING_CARD_SURFACE, LANDING_CHIP_SURFACE } from '@/landing/landing-surfaces'
import { Button } from '@/components/ui/button'
import { SectionTitle } from '@/components/ui/section-title'
import type { LandingFeatureItem, LandingSection } from '@/types/client-landing'
import { cn } from '@/lib/utils'

export type AppLandingSectionRendererProps = {
  section: LandingSection
  sectionIndex: number
}

function normalizeFeatureItem(item: string | LandingFeatureItem): LandingFeatureItem {
  if (typeof item === 'string') return { title: item }
  return item
}

export function AppLandingSectionRenderer({ section, sectionIndex }: AppLandingSectionRendererProps) {
  switch (section.type) {
    case 'features': {
      const headingId = `landing-features-heading-${sectionIndex}`
      return (
        <section
          className="w-full text-left"
          {...(section.props.title ? { 'aria-labelledby': headingId } : {})}
        >
          {section.props.title ? (
            <SectionTitle id={headingId} className="mb-5">
              {section.props.title}
            </SectionTitle>
          ) : null}
          <ul className="grid gap-4 md:grid-cols-2">
            {section.props.items.map((raw, i) => {
              const item = normalizeFeatureItem(raw)
              const Icon = getLandingIcon(item.icon)
              return (
                <li key={`${item.title}-${i}`} className={cn('flex gap-4', LANDING_CARD_SURFACE)}>
                  <Icon className="mt-0.5 h-5 w-5 shrink-0 text-primary" aria-hidden />
                  <div className="min-w-0 space-y-1">
                    <p className="text-sm font-semibold text-foreground">{item.title}</p>
                    {item.description ? (
                      <p className="text-sm leading-relaxed text-muted-foreground">{item.description}</p>
                    ) : null}
                  </div>
                </li>
              )
            })}
          </ul>
        </section>
      )
    }
    case 'steps': {
      const headingId = `landing-steps-heading-${sectionIndex}`
      return (
        <section
          className="w-full text-left"
          {...(section.props.title ? { 'aria-labelledby': headingId } : {})}
        >
          {section.props.title ? (
            <SectionTitle id={headingId} className="mb-5">
              {section.props.title}
            </SectionTitle>
          ) : null}
          <ol className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            {section.props.steps.map((step, i) => (
              <li key={step.title} className={LANDING_CARD_SURFACE}>
                <span className="text-xs font-semibold text-muted-foreground">Step {i + 1}</span>
                <p className="mt-2 text-sm font-semibold text-foreground">{step.title}</p>
                {step.description ? (
                  <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{step.description}</p>
                ) : null}
              </li>
            ))}
          </ol>
        </section>
      )
    }
    case 'ctaBand':
      return (
        <section className={cn('w-full text-center md:p-8', LANDING_CARD_SURFACE, 'p-6')}>
          {section.props.title ? (
            <h2 className="text-lg font-semibold text-foreground">{section.props.title}</h2>
          ) : null}
          {section.props.description ? (
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{section.props.description}</p>
          ) : null}
          <div className="mt-5">
            <Button asChild size="lg" pressFeedback>
              <LandingCtaLink to={section.props.cta.to}>{section.props.cta.label}</LandingCtaLink>
            </Button>
          </div>
        </section>
      )
    case 'logos': {
      const headingId = `landing-logos-heading-${sectionIndex}`
      return (
        <section
          className="w-full text-left"
          {...(section.props.title ? { 'aria-labelledby': headingId } : {})}
        >
          {section.props.title ? (
            <SectionTitle id={headingId} className="mb-5">
              {section.props.title}
            </SectionTitle>
          ) : null}
          <div className="flex flex-wrap gap-3">
            {section.props.captions.map((c) => (
              <span key={c} className={LANDING_CHIP_SURFACE}>
                {c}
              </span>
            ))}
          </div>
        </section>
      )
    }
    case 'quote':
      return (
        <blockquote
          id={`landing-quote-${sectionIndex}`}
          className={cn('w-full text-left md:p-8', LANDING_CARD_SURFACE, 'p-6 text-foreground')}
        >
          <p className="text-lg font-normal leading-relaxed text-foreground md:text-xl">
            &ldquo;{section.props.quote}&rdquo;
          </p>
          {section.props.attribution ? (
            <footer className="mt-3 text-sm text-muted-foreground">— {section.props.attribution}</footer>
          ) : null}
        </blockquote>
      )
    case 'splitNarrative':
    case 'waitlistBand':
      return null
    default:
      return null
  }
}
